/**
 * ModuleHub — Bir modülün alt öğelerini kart olarak listeler.
 * URL: `/modul/:slug`
 *
 * Home → ModuleHub → gerçek sayfa yol haritasının orta katmanıdır.
 * Kaynak: `/lib/moduleMenus.js` (Sidebar ile aynı config).
 */
import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { findModuleBySlug } from "@/lib/moduleMenus";
import { CaretRight, House } from "@phosphor-icons/react";

export default function ModuleHub() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "yonetici";
  const mod = findModuleBySlug(slug, isAdmin);

  if (!mod) return <Navigate to="/" replace />;

  const Icon = mod.icon;

  return (
    <Shell hideSiteSelector>
      <div className="space-y-8" data-testid={`module-hub-${mod.slug}`}>
        {/* Breadcrumb + Başlık */}
        <div>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-[#1B4776] transition-colors"
            data-testid="module-hub-breadcrumb-home"
          >
            <House size={12} weight="bold" />
            Karşılama Ekranı
          </Link>
          <div className="mt-3 border-l-4 border-[#95C11F] pl-4 flex items-start gap-4">
            <div className="w-14 h-14 flex items-center justify-center bg-[#1B4776] flex-shrink-0">
              <Icon size={30} weight="duotone" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#1B4776] font-heading">
                {mod.title}
              </h1>
              <p className="mt-1 text-sm text-slate-500 max-w-2xl">{mod.desc}</p>
            </div>
          </div>
        </div>

        {/* Alt öğe kartları */}
        {mod.items.length === 0 ? (
          <Card className="p-10 text-center text-slate-500 rounded-none sm:rounded-sm">
            Bu modülde alt sayfa bulunmuyor.
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {mod.items.map((it) => {
              const ItemIcon = it.icon;
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`module-hub-item-${it.testId}`}
                >
                  <Card className="group p-5 h-full border-l-4 border-l-[#95C11F]/60 hover:border-l-[#95C11F] hover:shadow-lg transition-all rounded-none sm:rounded-sm cursor-pointer bg-white">
                    <div className="flex items-start justify-between gap-4">
                      <div className="w-11 h-11 flex items-center justify-center bg-slate-100 group-hover:bg-[#1B4776] transition-colors">
                        <ItemIcon
                          size={22}
                          weight="duotone"
                          className="text-[#1B4776] group-hover:text-white transition-colors"
                        />
                      </div>
                      <div className="p-1.5 rounded-full bg-slate-50 group-hover:bg-[#95C11F] transition-colors">
                        <CaretRight
                          size={14}
                          weight="bold"
                          className="text-slate-400 group-hover:text-white transition-colors"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="text-sm font-bold text-[#0F172A] group-hover:text-[#1B4776] transition-colors">
                        {it.label}
                      </div>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Shell>
  );
}
